import { Component } from 'react';
import sharedStyles from '../single-page.module.scss';
import { genStyles } from '../converter-layout/generator-layout';
import styles from './html-table-converter.component.module.scss';

type Target = 'markdown' | 'csv' | 'json';

const SLUG_TO_TARGET: Record<string, Target> = {
    'convert-html-table-to-markdown': 'markdown',
    'convert-html-table-to-csv': 'csv',
    'convert-html-table-to-json': 'json',
};

const TARGET_LABEL: Record<Target, string> = {
    markdown: 'Markdown',
    csv: 'CSV',
    json: 'JSON',
};

const SAMPLE = `<table>
  <thead>
    <tr><th>Name</th><th>Role</th><th>Joined</th></tr>
  </thead>
  <tbody>
    <tr><td>Ada Lovelace</td><td>Mathematician</td><td>1843</td></tr>
    <tr><td>Alan Turing</td><td>Computer Scientist</td><td>1936</td></tr>
    <tr><td>Grace Hopper</td><td>Programmer</td><td>1944</td></tr>
  </tbody>
</table>`;

interface State {
    target: Target;
    input: string;
    error: string | null;
    copied: boolean;
}

interface ParsedTable {
    headers: string[];
    rows: string[][];
}

export default class HtmlTableConverterComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;

    constructor(props: { feature?: string }) {
        super(props);
        const target = SLUG_TO_TARGET[props.feature || ''] || 'markdown';
        this.state = {
            target,
            input: SAMPLE,
            error: null,
            copied: false,
        };
    }

    private parseTable(html: string): ParsedTable | null {
        try {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const table = doc.querySelector('table');
            if (!table) return null;
            const allRows = Array.from(table.querySelectorAll('tr'));
            if (allRows.length === 0) return null;
            // Detect header: <thead> rows OR first row's cells use <th>
            const theadRows = Array.from(table.querySelectorAll('thead tr'));
            let headers: string[] = [];
            let bodyRows: HTMLTableRowElement[];
            if (theadRows.length > 0) {
                headers = Array.from(theadRows[0].querySelectorAll('th, td')).map(c => c.textContent?.trim() || '');
                bodyRows = Array.from(table.querySelectorAll('tbody tr'));
                if (bodyRows.length === 0) bodyRows = allRows.slice(theadRows.length) as HTMLTableRowElement[];
            } else {
                const firstRowHasOnlyTh = allRows[0].querySelectorAll('th').length > 0 && allRows[0].querySelectorAll('td').length === 0;
                if (firstRowHasOnlyTh) {
                    headers = Array.from(allRows[0].querySelectorAll('th')).map(c => c.textContent?.trim() || '');
                    bodyRows = allRows.slice(1) as HTMLTableRowElement[];
                } else {
                    // No header — synthesize column1, column2, …
                    const colCount = allRows[0].children.length;
                    headers = Array.from({ length: colCount }, (_, i) => `column${i + 1}`);
                    bodyRows = allRows as HTMLTableRowElement[];
                }
            }
            const rows = bodyRows.map(r =>
                Array.from(r.querySelectorAll('td, th')).map(c => c.textContent?.trim() || '')
            );
            return { headers, rows };
        } catch {
            return null;
        }
    }

    private toMarkdown(t: ParsedTable): string {
        const headerRow = `| ${t.headers.join(' | ')} |`;
        const sep = `| ${t.headers.map(() => '---').join(' | ')} |`;
        const body = t.rows.map(r => `| ${r.join(' | ')} |`).join('\n');
        return [headerRow, sep, body].filter(Boolean).join('\n');
    }

    private toCsv(t: ParsedTable): string {
        const escape = (cell: string) => {
            if (/[",\n]/.test(cell)) return `"${cell.replace(/"/g, '""')}"`;
            return cell;
        };
        const lines = [t.headers.map(escape).join(',')];
        for (const r of t.rows) lines.push(r.map(escape).join(','));
        return lines.join('\n');
    }

    private toJson(t: ParsedTable): string {
        const objs = t.rows.map(r => {
            const o: Record<string, string> = {};
            t.headers.forEach((h, i) => { o[h] = r[i] ?? ''; });
            return o;
        });
        return JSON.stringify(objs, null, 2);
    }

    private get output(): string {
        const parsed = this.parseTable(this.state.input);
        if (!parsed) return '';
        switch (this.state.target) {
            case 'markdown': return this.toMarkdown(parsed);
            case 'csv': return this.toCsv(parsed);
            case 'json': return this.toJson(parsed);
        }
    }

    private setTarget = (target: Target) => this.setState({ target, copied: false });

    copyOutput = () => {
        const out = this.output;
        if (!out) return;
        navigator.clipboard.writeText(out).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    render() {
        const { target, input, copied } = this.state;
        const parsed = this.parseTable(input);
        const out = this.output;
        const noTable = !parsed && input.trim().length > 0;

        return (
            <div className="row">
                <div className="col-12">
                    <div className={genStyles.section}>
                        <div className={styles.targetTabs} role="tablist" aria-label="Output format">
                            {(Object.keys(TARGET_LABEL) as Target[]).map(t => (
                                <button key={t}
                                    role="tab"
                                    aria-selected={target === t}
                                    className={`${styles.targetTab} ${target === t ? styles.targetTabActive : ''}`}
                                    onClick={() => this.setTarget(t)}>
                                    {TARGET_LABEL[t]}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="col-12 col-md-6">
                    <div className={genStyles.section}>
                        <label className={sharedStyles.label}>HTML table input</label>
                        <textarea className={sharedStyles.textarea} rows={18} value={input}
                            spellCheck={false}
                            onChange={e => this.setState({ input: e.target.value, copied: false })}
                            aria-label="HTML table input" />
                        {noTable && <p className={styles.warning}>No `&lt;table&gt;` element found in the input.</p>}
                    </div>
                </div>
                <div className="col-12 col-md-6">
                    <div className={genStyles.section}>
                        <label className={sharedStyles.label}>{TARGET_LABEL[target]} output</label>
                        <textarea className={sharedStyles.textarea} readOnly rows={18} value={out}
                            placeholder="Paste an HTML table on the left to see the converted output here."
                            aria-label={`${TARGET_LABEL[target]} output`} />
                        <div className={styles.actions}>
                            <button className={`${sharedStyles.copyButton} ${copied ? sharedStyles.copyButtonSuccess : ''}`}
                                onClick={this.copyOutput} disabled={!out}>
                                {copied ? '✓ Copied' : 'Copy'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
